import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { AlertTriangle, Trash2, Loader2, FileX, Archive, CheckCircle2 } from "lucide-react";

/**
 * Pulizia in due tempi: prima si guarda cosa sparirebbe, poi si elimina. La
 * cancellazione è definitiva e tocca dati veri, quindi il pulsante resta
 * spento finché non si è visto l'elenco e scritto la conferma.
 *
 * Serve sia ai fascicoli del personale sia ai documenti dei mezzi: cambia
 * solo l'indirizzo da interrogare e come si chiama il proprietario.
 */
export default function PuliziaFascicoliDialog({
  onClose, onDone,
  base = "/employees/documents",
  titolo = "Pulizia dei fascicoli",
  intestazioneProprietario = "Dipendente",
}) {
  const [dati, setDati] = useState(null);
  const [errore, setErrore] = useState(null);
  const [senzaAllegato, setSenzaAllegato] = useState(true);
  const [archiviati, setArchiviati] = useState(true);
  const [conferma, setConferma] = useState("");
  const [busy, setBusy] = useState(false);
  const [espandi, setEspandi] = useState(null);

  useEffect(() => {
    api.get(`${base}/cleanup-preview`)
      .then(({ data }) => setDati(data))
      .catch((e) => setErrore(e.response?.data?.detail || "Impossibile leggere i documenti"));
  }, [base]);

  const totale = (senzaAllegato ? dati?.senza_allegato.conteggio || 0 : 0)
               + (archiviati ? dati?.archiviati.conteggio || 0 : 0);

  const esegui = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`${base}/cleanup`, {
        elimina_senza_allegato: senzaAllegato,
        elimina_archiviati: archiviati,
        conferma,
      });
      toast.success(`Eliminati ${data.eliminati} documenti · ${data.file_rimossi} file rimossi`);
      onDone?.();
      onClose();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Errore durante la pulizia");
    } finally { setBusy(false); }
  };

  const Gruppo = ({ chiave, icona: Icona, titolo, descrizione, attivo, setAttivo, gruppo, tono }) => (
    <div className={`border rounded p-3 ${attivo ? tono : "border-slate-200"}`}>
      <label className="flex items-start gap-2 cursor-pointer">
        <input type="checkbox" className="mt-1" checked={attivo}
               onChange={(e) => setAttivo(e.target.checked)} data-testid={`pulizia-${chiave}`} />
        <div className="min-w-0 flex-1">
          <div className="font-semibold text-sm flex items-center gap-2">
            <Icona className="w-4 h-4" /> {titolo}
            <Badge variant="outline" className="text-[10px]">{gruppo?.conteggio ?? 0}</Badge>
          </div>
          <p className="text-xs text-slate-600 mt-0.5">{descrizione}</p>
        </div>
      </label>
      {gruppo?.conteggio > 0 && (
        <>
          <button type="button" onClick={() => setEspandi(espandi === chiave ? null : chiave)}
                  className="text-xs text-orange-600 hover:underline mt-2">
            {espandi === chiave ? "Nascondi l'elenco" : "Vedi quali documenti"}
          </button>
          {espandi === chiave && (
            <div className="mt-2 max-h-56 overflow-y-auto border border-slate-200 rounded bg-white">
              <table className="w-full text-xs">
                <thead className="bg-slate-50 text-slate-500 sticky top-0">
                  <tr>
                    <th className="text-left p-1.5">{intestazioneProprietario}</th>
                    <th className="text-left p-1.5">Documento</th>
                    <th className="text-left p-1.5">Caricato da</th>
                  </tr>
                </thead>
                <tbody>
                  {gruppo.documenti.map(d => (
                    <tr key={d.id} className="border-t border-slate-100">
                      <td className="p-1.5">{d.proprietario}</td>
                      <td className="p-1.5">{d.titolo || d.categoria}</td>
                      <td className="p-1.5 text-slate-400 truncate max-w-[150px]">{d.caricato_da || "—"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}
    </div>
  );

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Trash2 className="w-5 h-5 text-red-600" /> {titolo}
          </DialogTitle>
        </DialogHeader>

        {errore && <p className="text-sm text-red-700 bg-red-50 border border-red-200 rounded p-3">{errore}</p>}
        {!dati && !errore && (
          <p className="text-sm text-slate-500 flex items-center gap-2 py-6 justify-center">
            <Loader2 className="w-4 h-4 animate-spin" /> Controllo dei fascicoli in corso...
          </p>
        )}

        {dati && (
          <div className="space-y-3">
            <div className="flex items-center gap-3 text-sm bg-slate-50 border border-slate-200 rounded p-3">
              <CheckCircle2 className="w-4 h-4 text-slate-400 flex-shrink-0" />
              <span>
                <strong>{dati.totale_documenti}</strong> documenti in archivio ·
                dopo la pulizia ne resterebbero <strong>{dati.totale_documenti - totale}</strong>
              </span>
            </div>

            <Gruppo chiave="senza-allegato" icona={FileX} attivo={senzaAllegato} setAttivo={setSenzaAllegato}
                    titolo="Documenti senza allegato" gruppo={dati.senza_allegato}
                    tono="border-amber-300 bg-amber-50"
                    descrizione="Registrazioni prive di file: quasi sempre residui delle prove iniziali. Controlla l'elenco: se qualcuno ha inserito a mano una voce vera senza caricare il documento, si trova qui e andrebbe persa." />

            <Gruppo chiave="archiviati" icona={Archive} attivo={archiviati} setAttivo={setArchiviati}
                    titolo="Documenti archiviati" gruppo={dati.archiviati}
                    tono="border-amber-300 bg-amber-50"
                    descrizione={`Messi da parte in passato, ${dati.archiviati.con_allegato} con il file allegato. Eliminandoli sparisce anche il file dal server.`} />

            <div className="border border-red-200 bg-red-50 rounded p-3 space-y-2">
              <p className="text-sm text-red-800 flex items-start gap-2">
                <AlertTriangle className="w-4 h-4 flex-shrink-0 mt-0.5" />
                <span>
                  Stai per eliminare <strong>{totale} documenti</strong> in modo definitivo,
                  file compresi. L'operazione non si annulla: l'unico ritorno è il salvataggio
                  notturno, che contiene la situazione di stanotte.
                </span>
              </p>
              <div>
                <label className="text-xs text-slate-600">Scrivi ELIMINA per confermare</label>
                <Input value={conferma} onChange={(e) => setConferma(e.target.value)}
                       placeholder="ELIMINA" className="mt-1" data-testid="pulizia-conferma" />
              </div>
            </div>
          </div>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Annulla</Button>
          <Button onClick={esegui} data-testid="pulizia-esegui"
                  disabled={busy || !dati || totale === 0 || conferma.trim().toUpperCase() !== "ELIMINA"}
                  className="bg-red-600 hover:bg-red-700 text-white">
            {busy ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Eliminazione...</>
                  : <><Trash2 className="w-4 h-4 mr-2" /> Elimina {totale} documenti</>}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
